import React, { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { vars } from 'src/core/themes';
import { ButtonProps, HighlightText } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ActivityLogClient,
  ApiException,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

export const LaunchUnspoilTickets = ({
  saleId,
  ticketIdsToUnspoil,
  marketplaceSaleId,
  variant,
  iconOnlyMode,
  disabled,
}: {
  saleId: number;
  ticketIdsToUnspoil?: number[];
  marketplaceSaleId?: string | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();

  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();

  const unspoilTicketsConfirmation = useContent(
    ContentId.UnspoilTicketsConfirmation
  );
  const onHoldText = useContent(ContentId.OnHold);
  const spoilTickets = useCallback(() => {
    setIsLoading(true);
    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);

        const result = await client.unspoilTicketsInSale(
          saleId,
          ticketIdsToUnspoil
        );
        if (!isSuccess(result)) {
          showErrorDialog(
            'SaleClient.unspoilTicketsInSale',
            {
              message: result.message ?? genericError,
              status: result.status!,
            } as ApiException,
            {
              trackErrorData: { saleId },
            }
          );
        } else {
          await setActivePosEntity(saleId, marketplaceSaleId, true);
        }
      },
      (error) => {
        showErrorDialog('SaleClient.unspoilTicketsInSale', error, {
          trackErrorData: { saleId },
        });
      },
      () => {
        setIsLoading(false);
        closeDialog();
      }
    );
  }, [
    activeAccountWebClientConfig,
    closeDialog,
    genericError,
    marketplaceSaleId,
    saleId,
    setActivePosEntity,
    showErrorDialog,
    ticketIdsToUnspoil,
  ]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      tryInvokeApi(
        async () => {
          await new ActivityLogClient(
            activeAccountWebClientConfig
          ).trackEntityView(saleId, ActionOutboxEntityType.Sale);
        },
        (error) => {
          trackError('ActivityLogClient.trackEntityView', error, { saleId });
        }
      );
      launchDialog();
    },
    [launchDialog, activeAccountWebClientConfig, saleId, trackError]
  );

  return (
    <div>
      <ButtonWithIcon
        onClick={onLaunchDialog}
        disabled={disabled || isLoading}
        icon={
          <WarningOutlineIcon
            withHoverEffect
            fill={
              !iconOnlyMode && variant == 'regular'
                ? IconsFill.textInverted
                : IconsFill.textBrand
            }
            size={vars.iconSize.m}
          />
        }
        iconOnlyMode={iconOnlyMode}
        variant={variant}
        textContentId={ContentId.UnspoilTickets}
      />
      <ConfirmDialog
        {...dialogProps}
        onOkay={spoilTickets}
        okText={ContentId.Save}
        disabled={isLoading}
        isLoading={isLoading}
        headerText={<Content id={ContentId.AreYouSure} />}
        bodyText={
          <HighlightText
            text={unspoilTicketsConfirmation}
            highlight={onHoldText}
          />
        }
      />
    </div>
  );
};
